import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../config';

const buildQuery = (dateFrom, dateTo, orderStatuses, referralBotId) => {
    const p = new URLSearchParams();
    if (dateFrom) p.set('date_from', `${dateFrom}T00:00:00`);
    if (dateTo) p.set('date_to', `${dateTo}T23:59:59`);
    if (orderStatuses.trim()) p.set('order_statuses', orderStatuses.trim());
    if (referralBotId.trim()) p.set('referral_bot_id', referralBotId.trim());
    return p.toString();
};

const AnalyticsPage = () => {
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [orderStatuses, setOrderStatuses] = useState('');
    const [referralBotId, setReferralBotId] = useState('');
    const [utmFrom, setUtmFrom] = useState('');
    const [utmTo, setUtmTo] = useState('');
    const [utmRows, setUtmRows] = useState([]);
    const [utmLoading, setUtmLoading] = useState(false);
    const [logItems, setLogItems] = useState([]);
    const [logTotal, setLogTotal] = useState(0);
    const [logOffset, setLogOffset] = useState(0);
    const logLimit = 50;

    const query = useMemo(
        () => buildQuery(dateFrom, dateTo, orderStatuses, referralBotId),
        [dateFrom, dateTo, orderStatuses, referralBotId],
    );

    const openExport = (path, isXlsx = false) => {
        const sep = path.includes('?') ? '&' : '?';
        const url = `${API_BASE_URL}${path}${query ? `${path.includes('?') ? '&' : '?'}${query}` : ''}`;
        const finalUrl = isXlsx && !path.endsWith('.xlsx') ? `${url}${query ? '&' : '?'}dummy=1`.replace(/dummy=1$/, '').replace(/\?$/, '') : url;
        // endpoints already include .xlsx in path
        window.open(finalUrl, '_blank');
    };

    const openOrdersCsv = () => window.open(`${API_BASE_URL}/api/analytics/export/orders${query ? `?${query}` : ''}`, '_blank');
    const openOrdersXlsx = () => window.open(`${API_BASE_URL}/api/analytics/export/orders.xlsx${query ? `?${query}` : ''}`, '_blank');
    const openRefCsv = () => window.open(`${API_BASE_URL}/api/analytics/export/referral-bots`, '_blank');
    const openRefXlsx = () => window.open(`${API_BASE_URL}/api/analytics/export/referral-bots.xlsx`, '_blank');
    const openPrCsv = () => window.open(`${API_BASE_URL}/api/analytics/export/purchase-requests${query ? `?${query}` : ''}`, '_blank');
    const openPrXlsx = () => window.open(`${API_BASE_URL}/api/analytics/export/purchase-requests.xlsx${query ? `?${query}` : ''}`, '_blank');
    const openUtmCsv = () => {
        const p = new URLSearchParams();
        if (utmFrom) p.set('date_from', `${utmFrom}T00:00:00`);
        if (utmTo) p.set('date_to', `${utmTo}T23:59:59`);
        window.open(`${API_BASE_URL}/api/analytics/export/utm-all${p.toString() ? `?${p}` : ''}`, '_blank');
    };
    const openUtmXlsx = () => {
        const p = new URLSearchParams();
        if (utmFrom) p.set('date_from', `${utmFrom}T00:00:00`);
        if (utmTo) p.set('date_to', `${utmTo}T23:59:59`);
        window.open(`${API_BASE_URL}/api/analytics/export/utm-all.xlsx${p.toString() ? `?${p}` : ''}`, '_blank');
    };

    const loadUtmAggregate = useCallback(async () => {
        try {
            setUtmLoading(true);
            const p = new URLSearchParams();
            if (utmFrom) p.set('date_from', `${utmFrom}T00:00:00`);
            if (utmTo) p.set('date_to', `${utmTo}T23:59:59`);
            const r = await fetch(`${API_BASE_URL}/api/analytics/utm-aggregate${p.toString() ? `?${p}` : ''}`, {
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            });
            if (!r.ok) throw new Error('aggregate failed');
            const data = await r.json();
            setUtmRows(Array.isArray(data.items) ? data.items : []);
        } catch (e) {
            console.error(e);
            setUtmRows([]);
        } finally {
            setUtmLoading(false);
        }
    }, [utmFrom, utmTo]);

    useEffect(() => {
        loadUtmAggregate();
    }, [loadUtmAggregate]);

    const loadAutofunnelLog = useCallback(async () => {
        try {
            const r = await fetch(
                `${API_BASE_URL}/api/analytics/autofunnel-log?limit=${logLimit}&offset=${logOffset}`,
                { credentials: 'include', headers: { 'Content-Type': 'application/json' } },
            );
            if (!r.ok) return;
            const data = await r.json();
            setLogItems(data.items || []);
            setLogTotal(data.total || 0);
        } catch (_) {}
    }, [logOffset]);

    useEffect(() => {
        loadAutofunnelLog();
    }, [loadAutofunnelLog]);

    return (
        <div className="space-y-10">
            <h2 className="text-2xl font-bold text-slate-800">Аналитика и выгрузки</h2>

            <section className="space-y-3">
                <h3 className="text-lg font-semibold text-slate-800">Фильтры (заказы и заявки)</h3>
                <div className="bg-white rounded-xl border border-slate-100 p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div>
                        <label className="text-xs text-slate-500">Дата с</label>
                        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-full rounded border border-slate-200 px-3 py-2 mt-1" />
                    </div>
                    <div>
                        <label className="text-xs text-slate-500">Дата по</label>
                        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-full rounded border border-slate-200 px-3 py-2 mt-1" />
                    </div>
                    <div>
                        <label className="text-xs text-slate-500">Статусы заказов (через запятую)</label>
                        <input
                            type="text"
                            value={orderStatuses}
                            onChange={(e) => setOrderStatuses(e.target.value)}
                            placeholder="success, pending, cancelled"
                            className="w-full rounded border border-slate-200 px-3 py-2 mt-1 text-sm"
                        />
                    </div>
                    <div>
                        <label className="text-xs text-slate-500">ID реф. бота (опционально)</label>
                        <input
                            type="text"
                            value={referralBotId}
                            onChange={(e) => setReferralBotId(e.target.value)}
                            placeholder="например 12"
                            className="w-full rounded border border-slate-200 px-3 py-2 mt-1 text-sm"
                        />
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    <button type="button" onClick={openOrdersCsv} className="bg-indigo-600 text-white rounded-lg px-4 py-3 hover:bg-indigo-700">
                        История покупок (CSV)
                    </button>
                    <button type="button" onClick={openOrdersXlsx} className="bg-indigo-500 text-white rounded-lg px-4 py-3 hover:bg-indigo-600">
                        История покупок (XLSX)
                    </button>
                    <button type="button" onClick={openRefCsv} className="bg-slate-800 text-white rounded-lg px-4 py-3 hover:bg-slate-900">
                        Реферальные боты (CSV)
                    </button>
                    <button type="button" onClick={openRefXlsx} className="bg-slate-600 text-white rounded-lg px-4 py-3 hover:bg-slate-700">
                        Реферальные боты (XLSX)
                    </button>
                    <button type="button" onClick={openPrCsv} className="bg-emerald-600 text-white rounded-lg px-4 py-3 hover:bg-emerald-700">
                        Все заявки на покупку (CSV)
                    </button>
                    <button type="button" onClick={openPrXlsx} className="bg-emerald-500 text-white rounded-lg px-4 py-3 hover:bg-emerald-600">
                        Все заявки на покупку (XLSX)
                    </button>
                </div>
            </section>

            <section className="space-y-3">
                <h3 className="text-lg font-semibold text-slate-800">Свод по всем UTM (все реф. боты)</h3>
                <p className="text-sm text-slate-500">Укажите период для колонок «за период»; без дат — только накопительные метрики.</p>
                <div className="flex flex-wrap gap-2 items-end">
                    <div>
                        <label className="text-xs text-slate-500 block">Период с</label>
                        <input type="date" value={utmFrom} onChange={(e) => setUtmFrom(e.target.value)} className="rounded border border-slate-200 px-3 py-2" />
                    </div>
                    <div>
                        <label className="text-xs text-slate-500 block">Период по</label>
                        <input type="date" value={utmTo} onChange={(e) => setUtmTo(e.target.value)} className="rounded border border-slate-200 px-3 py-2" />
                    </div>
                    <button type="button" onClick={loadUtmAggregate} className="px-4 py-2 bg-slate-800 text-white rounded-lg text-sm">
                        Обновить таблицу
                    </button>
                    <button type="button" onClick={openUtmCsv} className="px-4 py-2 bg-cyan-700 text-white rounded-lg text-sm">
                        Скачать CSV
                    </button>
                    <button type="button" onClick={openUtmXlsx} className="px-4 py-2 bg-cyan-600 text-white rounded-lg text-sm">
                        Скачать XLSX
                    </button>
                </div>
                <div className="overflow-x-auto bg-white rounded-xl border border-slate-100">
                    {utmLoading ? (
                        <div className="p-6 text-slate-500">Загрузка…</div>
                    ) : (
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr className="text-left border-b bg-slate-50">
                                    <th className="p-2">Бот</th>
                                    <th className="p-2">Название</th>
                                    <th className="p-2">utm_key</th>
                                    <th className="p-2">Клики</th>
                                    <th className="p-2">Уники</th>
                                    <th className="p-2">Заказы</th>
                                    <th className="p-2">Выручка</th>
                                    <th className="p-2">За период: уники</th>
                                    <th className="p-2">Заказы</th>
                                    <th className="p-2">Выручка</th>
                                </tr>
                            </thead>
                            <tbody>
                                {utmRows.map((u) => (
                                    <tr key={u.id} className="border-b border-slate-100">
                                        <td className="p-2">@{u.bot_username || '—'}</td>
                                        <td className="p-2">{u.name}</td>
                                        <td className="p-2 font-mono text-xs">{u.utm_key}</td>
                                        <td className="p-2">{u.clicks}</td>
                                        <td className="p-2">{u.unique_users}</td>
                                        <td className="p-2">{u.orders_count}</td>
                                        <td className="p-2">{Number(u.revenue || 0).toFixed(2)}</td>
                                        <td className="p-2">{u.period ? u.period.period_new_uniques : '—'}</td>
                                        <td className="p-2">{u.period ? u.period.period_orders : '—'}</td>
                                        <td className="p-2">{u.period ? Number(u.period.period_revenue || 0).toFixed(2) : '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </section>

            <section className="space-y-3">
                <h3 className="text-lg font-semibold text-slate-800">Журнал автоворонки</h3>
                <p className="text-sm text-slate-500">Факт отправки сообщений (идемпотентность в БД по dedupe_key).</p>
                <div className="flex gap-2 mb-2">
                    <button
                        type="button"
                        disabled={logOffset <= 0}
                        onClick={() => setLogOffset((o) => Math.max(0, o - logLimit))}
                        className="px-3 py-1 border rounded-lg text-sm disabled:opacity-40"
                    >
                        Назад
                    </button>
                    <button
                        type="button"
                        disabled={logOffset + logLimit >= logTotal}
                        onClick={() => setLogOffset((o) => o + logLimit)}
                        className="px-3 py-1 border rounded-lg text-sm disabled:opacity-40"
                    >
                        Вперёд
                    </button>
                    <span className="text-sm text-slate-500 self-center">
                        {logTotal ? `${logOffset + 1}–${Math.min(logOffset + logLimit, logTotal)} из ${logTotal}` : '—'}
                    </span>
                </div>
                <div className="overflow-x-auto bg-white rounded-xl border border-slate-100">
                    <table className="min-w-full text-sm">
                        <thead>
                            <tr className="text-left border-b bg-slate-50">
                                <th className="p-2">Время</th>
                                <th className="p-2">Сценарий</th>
                                <th className="p-2">Пользователь</th>
                                <th className="p-2">dedupe_key</th>
                                <th className="p-2">ref_bot</th>
                                <th className="p-2">order</th>
                            </tr>
                        </thead>
                        <tbody>
                            {logItems.map((row) => (
                                <tr key={row.id} className="border-b border-slate-100">
                                    <td className="p-2 whitespace-nowrap">{row.sent_at ? new Date(row.sent_at).toLocaleString('ru-RU') : '—'}</td>
                                    <td className="p-2">{row.scenario}</td>
                                    <td className="p-2">
                                        {row.user_id}
                                        {row.username ? ` @${row.username}` : ''}
                                    </td>
                                    <td className="p-2 font-mono text-xs max-w-xs truncate" title={row.dedupe_key}>
                                        {row.dedupe_key}
                                    </td>
                                    <td className="p-2">{row.referral_bot_id ?? '—'}</td>
                                    <td className="p-2">{row.context_order_id ?? '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
};

export default AnalyticsPage;
